// src/components/auth-form.tsx — shared email+password form (Open Sans is
// global; neutral theme tokens). Maps server error codes to friendly copy.

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ApiError, postReactivate } from '@/lib/kwykedit-api'

const inputClassName =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'

export function AuthForm({
  submitLabel,
  onSubmit,
  switchPrompt,
  switchTo,
  switchLabel,
  onAccountReactivated,
}: {
  submitLabel: string
  onSubmit: (email: string, password: string) => Promise<void>
  switchPrompt: string
  switchTo: '/login' | '/signup'
  switchLabel: string
  onAccountReactivated?: () => Promise<void> | void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [reactivating, setReactivating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    setErrorCode(null)
    void onSubmit(email.trim(), password)
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setError(err.message)
          setErrorCode(err.code ?? null)
        } else {
          setError('Something went wrong — please try again.')
          setErrorCode(null)
        }
      })
      .finally(() => setPending(false))
  }

  const showLoginHint =
    errorCode === 'EMAIL_EXISTS' || errorCode === 'EMAIL_REUSED' || errorCode === 'ACCOUNT_CLOSED'
  const showReactivate = errorCode === 'ACCOUNT_CLOSED'

  const handleReactivate = () => {
    if (reactivating) return
    setReactivating(true)
    setError(null)
    void postReactivate(email.trim(), password)
      .then(() => onAccountReactivated?.())
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not reactivate your account.')
      })
      .finally(() => setReactivating(false))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="auth-email" className="text-sm font-medium">
          Email
        </label>
        <input
          id="auth-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClassName}
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="auth-password" className="text-sm font-medium">
          Password
        </label>
        <input
          id="auth-password"
          type="password"
          autoComplete={switchTo === '/login' ? 'new-password' : 'current-password'}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="8+ characters"
          className={inputClassName}
        />
      </div>
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <p>{error}</p>
          {showLoginHint && (
            <p className="mt-1">
              <Link to="/login" className="underline hover:opacity-80">
                Go to log in →
              </Link>
            </p>
          )}
          {showReactivate && (
            <div className="mt-2">
              <p className="mb-2 text-destructive">
                This account was closed. Reactivate it with the same password, then
                pick a plan — billing does not resume on its own.
              </p>
              <Button
                type="button"
                className="w-full"
                disabled={reactivating}
                onClick={handleReactivate}
              >
                {reactivating ? 'Reactivating…' : 'Reactivate my account'}
              </Button>
            </div>
          )}
        </div>
      )}
      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? 'Please wait…' : submitLabel}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        {switchPrompt}{' '}
        <Link to={switchTo} className="text-foreground underline hover:opacity-80">
          {switchLabel}
        </Link>{' '}
        ·{' '}
        <Link to="/pricing" className="underline hover:opacity-80">
          Pricing
        </Link>
      </p>
    </form>
  )
}
